
import { HttpParams } from '@angular/common/http';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Component, EventEmitter, Input, OnInit, Output, ChangeDetectorRef, inject, DestroyRef ,
  ChangeDetectionStrategy,
} from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { ActionService } from '../../../shared/_services/action.service';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import { environment } from '../../../../environments/environment';
import { safeJsonInput } from '../../../shared/_utils/safe-json-input';

interface QuarterRow {
  TBEPS_TAX_QTR_KEY: number;
  TAX_YEAR: number | string;
  QUARTER: number | string;
  ACTIVE: number | string;
  S3_CLIENT_KEY?: number | string | null;
}

interface TaxYearOption {
  label: string;
  value: string;
}

@Component({
    selector: 'gtw-cbc-beps-quarters',
    templateUrl: './beps-quarters.component.html',
    styleUrls: ['./beps-quarters.component.scss'],
    standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class BepsQuartersComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);

  // Legacy back-compat: the GTW12 host passed a JSON string via `gtw-input`.
  // The new CE host (CeHostDialogComponent) does NOT set this; context comes
  // from WorkspaceService.activeScreen.data instead. Kept as a harmless no-op
  // so an accidental legacy attribute never throws.
  @Input('gtw-input')
  set gtwInputStr(input: string) {
    try {
      safeJsonInput<any>(input, null, 'beps-quarters.parse1');
    } catch (e) {
      console.error('Error parsing gtwInput', e);
    }
  }

  @Output('close-modal') closeModal = new EventEmitter();
  @Output('request-grid-update') requestGridUpdate = new EventEmitter<string>();

  quartersForm!: UntypedFormGroup;
  taxYears: TaxYearOption[] = [];

  constructor(
    private formBuilder: UntypedFormBuilder,
    private actionService: ActionService,
    private alertService: GtwSnackbarService,
    private gtwGlobal: GlobalService,
    private workspaceService: WorkspaceService,
    private appService: AppService,
    private cdr: ChangeDetectorRef,
  ) { }

  ngOnInit(): void {
    this.taxYears = this.loadTaxYears();

    this.quartersForm = this.formBuilder.group({
      TAX_YEAR: [this.taxYears[0]?.label ?? '', Validators.required],
      QUARTER: [1, Validators.required],
      ACTIVE: [1, Validators.required],
    });

    if (this.isEdit) {
      const row = this.selectedRow;
      this.quartersForm.patchValue({
        TAX_YEAR: row.TAX_YEAR,
        QUARTER: row.QUARTER,
        ACTIVE: Number(row.ACTIVE) ? 1 : 0,
      });
      this.quartersForm.get('TAX_YEAR')?.disable();
      this.quartersForm.get('QUARTER')?.disable();
    }

    this.cdr.detectChanges();
  }

  private loadTaxYears(): TaxYearOption[] {
    const values = this.gtwGlobal?.global?.inputs?.tax_year?.values;
    return Array.isArray(values) ? values : [];
  }

  private get activeData(): any {
    return this.workspaceService.workspaceFactory?.activeScreen?.data || {};
  }

  get isEdit(): boolean {
    const modalName = this.activeData.modalName || this.activeData.modal_name;
    return modalName === 'updateBepsAdminQuarter';
  }

  private get selectedRow(): QuarterRow {
    const gridData = this.activeData.gridData;
    return this.activeData.rowData
      || (Array.isArray(gridData) ? (gridData[0]?.data || gridData[0]) : null)
      || {} as QuarterRow;
  }

  private get clientKey(): number {
    const rawClientKey = this.selectedRow?.S3_CLIENT_KEY ?? this.appService.USER_SETTINGS?.user?.client_key ?? 0;
    return Number(rawClientKey) || 0;
  }

  onSubmit(): void {
    if (!this.quartersForm.valid) {
      return;
    }

    // Disabled controls (TAX_YEAR/QUARTER in edit mode) are intentionally
    // excluded from `.value`: the record is identified by TBEPS_TAX_QTR_KEY,
    // matching the legacy AngularJS behavior.
    const params = new HttpParams({
      fromObject: {
        ...this.quartersForm.value,
        ACTIVE: this.quartersForm.get('ACTIVE')?.value ? '1' : '0',
        ...(this.isEdit ? { TBEPS_TAX_QTR_KEY: this.selectedRow.TBEPS_TAX_QTR_KEY } : {}),
      }
    });

    this.actionService.saveActionData(environment.base_url, 33283, this.clientKey, params)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((response: any) => {
        if (response.callSuccess === '1') {
          this.alertService.openSnackBar('Data saved successfully', 'gtw-snackbar--green');
          this.requestGridUpdate.emit('refresh');
        } else {
          this.alertService.openSnackBar('Failed to save data', 'gtw-snackbar--red');
          this.cancel();
        }
      });
  }

  cancel(): void {
    this.requestGridUpdate.emit('cancel');
  }
}
